import React from "react";
import { useParams, useHistory } from "react-router-dom";
import { QueryLoader, toast } from "@jsq/ds/core";
import {
  useGetQuestionnaireQuery,
  useUpdateAmlEntityTypeMutation,
} from "../../graphql/supergraph.index";
import { EntitySurveyPage } from "../EntitySurveyPage";
import { provideLegalName } from "../ProfileTree/utils";
import { InstitutionEditScreenProps } from "./types";

const setQuestionsAnswers = (
  questions: Array<{
    id: string;
    question: string;
    targetAmlEntityTypeId: number;
    answer: string;
  }>,
  entityTypeId: number | null,
) => {
  if (entityTypeId === null) {
    return questions;
  }
  const questionsWithAnswers = [...questions];
  let hasMatched = false;

  questionsWithAnswers.reverse().forEach((question) => {
    if (hasMatched || question.targetAmlEntityTypeId === entityTypeId) {
      question.answer = "Yes";
      hasMatched = true;
    } else {
      question.answer = "No";
    }
  });

  return questionsWithAnswers;
};

export const InstitutionEditScreen: React.FC<InstitutionEditScreenProps> = ({
  caseResult,
  basePath,
}) => {
  const { profileId } = useParams<{ profileId: string }>();
  const history = useHistory();

  const allProfiles = caseResult?.complianceCasesByObjectId[0].profiles.concat([
    caseResult?.complianceCasesByObjectId[0].investingProfile,
  ]);
  const caseId = caseResult?.complianceCasesByObjectId[0].id;
  const questionsResult = useGetQuestionnaireQuery({
    variables: { caseId: caseId || 0 },
    skip: !caseId,
  });
  const profile = allProfiles?.filter((p) => p?.id === parseInt(profileId))[0];

  const [updateAmlEntityType] = useUpdateAmlEntityTypeMutation();

  if (!profile) {
    throw new Error("Profile not found");
  }

  const nonDefaultQuestions =
    questionsResult.data?.complianceCase?.investingProfile?.amlEntityTypeQuestions
      .filter((q) => !q?.isDefault)
      .map((q) => {
        return {
          id: q?.id.toString(),
          question: q?.text || "",
          answer: "",
          targetAmlEntityTypeId: q?.targetAmlEntityTypeId,
        };
      });

  const defaultQuestions =
    questionsResult.data?.complianceCase?.investingProfile?.amlEntityTypeQuestions
      .filter((q) => q?.isDefault)
      .map((q) => {
        return { targetAmlEntityTypeId: q?.targetAmlEntityTypeId };
      });
  const nonDefaultQuestionsWithAnswers = setQuestionsAnswers(
    nonDefaultQuestions || [],
    profile.amlEntityTypeId,
  );
  return (
    <QueryLoader result={questionsResult}>
      <EntitySurveyPage
        questions={nonDefaultQuestionsWithAnswers || []}
        initialAmlEntityTypeId={profile.amlEntityTypeId}
        entityName={provideLegalName(profile)}
        onCompleted={async (values) => {
          const positiveAnswers = values.updatedQuestions.filter(
            (question) => question.answer === "Yes",
          );

          const newAmlEntityTypeId =
            positiveAnswers.length > 0
              ? (nonDefaultQuestionsWithAnswers?.find((q) => q.id === positiveAnswers[0].id)
                  ?.targetAmlEntityTypeId as number)
              : defaultQuestions![0].targetAmlEntityTypeId;
          if (newAmlEntityTypeId !== profile.amlEntityTypeId) {
            await updateAmlEntityType({
              variables: {
                profileId: parseInt(profileId),
                amlEntityTypeId: newAmlEntityTypeId,
                caseId: caseId || 0,
              },
              onCompleted: () => {
                toast.success("Answer recorded successfully");
                history.push(`${basePath}/overview/${profileId}`);
              },
              onError: () => {
                toast.error(`Error updating AML entity type`);
              },
            });
          } else {
            history.push(`${basePath}/overview/${profileId}`);
          }
        }}
        onBack={() => {
          history.push(`${basePath}/overview/${profileId}`);
        }}
      />
    </QueryLoader>
  );
};
